import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Icon, Modal, StatusPulse, CopyButton, CodeToken, ServerLogo, Select, SelectItem } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface MCPServersProps {
  apiUrl: string;
}

interface MCPServer {
  server_id: string;
  org_id: string;
  name: string;
  hostname: string;
  port: number;
  protocol: 'http' | 'https';
  status: 'pending' | 'validated' | 'active' | 'disabled' | 'unreachable';
  base_path: string;
  auth_type: string;
  created_at: string;
  validated_at?: string;
  health_status: 'healthy' | 'unhealthy' | 'unknown';
  last_health_check?: string;
  /** 'direct' (firewall reaches it itself) or 'brokered' (relayed via an on-prem sensor). */
  access_mode?: 'direct' | 'brokered';
  sensor_id?: string | null;
}

/** An enrolled on-prem broker sensor that can front unreachable MCP servers. */
interface BrokerSensor {
  sensor_id: string;
  name: string;
  status: 'active' | 'revoked';
  last_poll_at?: string | null;
  created_at: string;
  connected: boolean;
}

interface MCPTool {
  tool_id: string;
  name: string;
  description: string;
}

interface AvailableService {
  name: string;
  type: 'hardcoded' | 'discovered';
  gateway_uri: string;
  status?: string;
  health_status?: string;
  tools?: string[];
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-redact',
  validated: 'badge-info',
  active: 'badge-success',
  disabled: 'badge-unknown',
  unreachable: 'badge-error',
};

const MCPServers: React.FC<MCPServersProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [availableServices, setAvailableServices] = useState<AvailableService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedServer, setSelectedServer] = useState<MCPServer | null>(null);
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  const [showAddServerModal, setShowAddServerModal] = useState(false);
  const [showServerDetailsModal, setShowServerDetailsModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [discoveryAddresses, setDiscoveryAddresses] = useState<string>('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState<any>(null);
  const [serverTools, setServerTools] = useState<MCPTool[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [validating, setValidating] = useState<string | null>(null);
  const [healthChecking, setHealthChecking] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [existingAuthType, setExistingAuthType] = useState<string | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [notificationModal, setNotificationModal] = useState<{ open: boolean; title: string; message: string; details?: React.ReactNode; actionLabel?: string; onAction?: () => void }>({ open: false, title: '', message: '' });

  // --- MCP Broker: on-prem servers reached via a long-polling sensor ---
  const [brokerSensors, setBrokerSensors] = useState<BrokerSensor[]>([]);
  const [showBrokerModal, setShowBrokerModal] = useState(false);
  const [accessServer, setAccessServer] = useState<MCPServer | null>(null);
  const [accessMode, setAccessMode] = useState<'direct' | 'brokered'>('direct');
  const [accessSensorId, setAccessSensorId] = useState<string>('');
  const [savingAccess, setSavingAccess] = useState(false);
  const [togglingActive, setTogglingActive] = useState<string | null>(null);
  const [newSensorName, setNewSensorName] = useState('');
  const [registeringSensor, setRegisteringSensor] = useState(false);
  const [issuedToken, setIssuedToken] = useState<{ sensor_id: string; token: string } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    hostname: '',
    port: 3000,
    protocol: 'http',
    auth_type: 'none',
  });

  const [authFormData, setAuthFormData] = useState({
    auth_type: 'basic',
    username: '',
    password: '',
    token: '',
    secret: '',
    algorithm: 'HS256',
    client_id: '',
    client_secret: '',
    token_url: '',
  });

  useEffect(() => {
    fetchServers();
    fetchAvailableServices();
    fetchBrokerSensors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, statusFilter]);

  const fetchBrokerSensors = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/mcp-broker/sensors`);
      if (response.ok) {
        const data = await response.json();
        setBrokerSensors(data.sensors || []);
      }
    } catch (err) {
      console.error('Failed to fetch broker sensors:', err);
    }
  };

  const handleRegisterSensor = async () => {
    if (!newSensorName.trim()) return;
    try {
      setRegisteringSensor(true);
      const response = await fetch(`${apiUrl}/admin/mcp-broker/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newSensorName.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Registration failed');
      // Shown exactly once — it's hashed at rest and can't be recovered.
      setIssuedToken({ sensor_id: data.sensor_id, token: data.token });
      setNewSensorName('');
      fetchBrokerSensors();
    } catch (err: any) {
      setNotificationModal({ open: true, title: t('pages.mCPServers.registrationFailed'), message: err.message });
    } finally {
      setRegisteringSensor(false);
    }
  };

  const handleRevokeSensor = async (sensorId: string) => {
    try {
      await fetch(`${apiUrl}/admin/mcp-broker/sensors/${sensorId}/revoke`, { method: 'POST' });
      fetchBrokerSensors();
    } catch (err: any) {
      setNotificationModal({ open: true, title: t('pages.mCPServers.revokeFailed'), message: err.message });
    }
  };

  /**
   * Activate / deactivate a server. Validation only moves a server to
   * `validated`, but every routing path (available-services, loadServices)
   * requires `active` — so without this the server is stranded: healthy,
   * validated, and invisible to agents.
   */
  const handleSetActive = async (server: MCPServer, active: boolean) => {
    try {
      setTogglingActive(server.server_id);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${server.server_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: active ? 'active' : 'disabled' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Update failed');
      await fetchServers();
      await fetchAvailableServices();
    } catch (err: any) {
      setNotificationModal({
        open: true,
        title: active ? 'Could not activate server' : 'Could not deactivate server',
        message: err.message,
      });
    } finally {
      setTogglingActive(null);
    }
  };

  const handleOpenAccessModal = (server: MCPServer) => {
    setAccessServer(server);
    setAccessMode(server.access_mode === 'brokered' ? 'brokered' : 'direct');
    setAccessSensorId(server.sensor_id || '');
  };

  const handleSaveAccess = async () => {
    if (!accessServer) return;
    try {
      setSavingAccess(true);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${accessServer.server_id}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_mode: accessMode,
          sensor_id: accessMode === 'brokered' ? accessSensorId : null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Update failed');
      setAccessServer(null);
      fetchServers();
    } catch (err: any) {
      setNotificationModal({ open: true, title: t('pages.mCPServers.couldNotUpdateAccessMode'), message: err.message });
    } finally {
      setSavingAccess(false);
    }
  };

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError(null);
      const url =
        statusFilter === 'all'
          ? `${apiUrl}/admin/mcp/servers`
          : `${apiUrl}/admin/mcp/servers?status=${statusFilter}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setServers(data.servers || []);
      } else {
        setError('Failed to fetch servers');
      }
    } catch (error) {
      console.error('Failed to fetch servers:', error);
      setError('Error loading servers. Make sure the firewall is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableServices = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/mcp/available-services`);
      if (response.ok) {
        const data = await response.json();
        setAvailableServices(data.services || []);
      }
    } catch (error) {
      console.error('Failed to fetch available services:', error);
    }
  };

  const handleDiscoverServers = async () => {
    if (!discoveryAddresses.trim()) {
      setError('Please enter at least one address');
      return;
    }
    try {
      setIsDiscovering(true);
      setError(null);
      const addresses = discoveryAddresses
        .split('\n')
        .map((addr) => addr.trim())
        .filter((addr) => addr.length > 0);
      const response = await fetch(`${apiUrl}/admin/mcp/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses }),
      });
      if (response.ok) {
        const data = await response.json();
        setDiscoveryResults(data);
        setDiscoveryAddresses('');
        setShowDiscoveryModal(false);
        await new Promise((resolve) => setTimeout(resolve, 500));
        fetchServers();
      } else {
        const data = await response.json();
        setError(data.message || 'Discovery failed');
      }
    } catch (error) {
      console.error('Discovery error:', error);
      setError('Error during discovery');
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleAddServer = async () => {
    if (!formData.name || !formData.hostname || !formData.port) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setError(null);
      const response = await fetch(`${apiUrl}/admin/mcp/servers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowAddServerModal(false);
        setFormData({ name: '', hostname: '', port: 3000, protocol: 'http', auth_type: 'none' });
        fetchServers();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add server');
      }
    } catch (error) {
      console.error('Add server error:', error);
      setError('Error adding server');
    }
  };

  const handleValidateServer = async (serverId: string) => {
    try {
      setValidating(serverId);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${serverId}/validate`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        const matchedServer = servers.find((s) => s.server_id === serverId);
        const authConfigured = data.auth_checked;
        const authType = data.auth_type || 'none';
        setNotificationModal({
          open: true,
          title: t('pages.mCPServers.discoveryValidationResult'),
          message: t('pages.mCPServers.serverDiscoveryValidationCompleted'),
          details: (
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
              <p><strong>{t('pages.mCPServers.scope')}</strong> {data.validation_scope || t('pages.mCPServers.discovery')}</p>
              <p>
                <strong>{t('pages.mCPServers.auth')}</strong>{' '}
                {authConfigured
                  ? <span style={{ color: 'var(--success)', fontWeight: 600 }}>{t('pages.mCPServers.configured', { v0: authType })}</span>
                  : <span style={{ color: 'var(--text-2)' }}>{t('pages.mCPServers.noneServerAllowsUnauthenticatedAccess')}</span>
                }
              </p>
              <p><strong>{t('pages.mCPServers.valid')}</strong> {data.valid ? t('pages.mCPServers.yes') : t('pages.mCPServers.no')}</p>
              <p><strong>{t('pages.mCPServers.health')}</strong> {data.health}</p>
              <p><strong>{t('pages.mCPServers.tools')}</strong> {data.tools_count || 0}</p>
              <p><strong>{t('pages.mCPServers.responseTime')}</strong> {data.response_time_ms}{t('pages.mCPServers.msUnit')}</p>
              <p><strong>{t('pages.mCPServers.note')}</strong> {t('pages.mCPServers.thisValidatesMCPDiscoveryToolListing')}</p>
              {!authConfigured && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--surface-2, rgba(0,0,0,0.04))', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }}>
                  {t('pages.mCPServers.noAuthCredentialsAreConfiguredIf')} <strong>{t('pages.mCPServers.configureAuth')}</strong> {t('pages.mCPServers.toAddThem')}
                </div>
              )}
            </div>
          ),
          ...(!authConfigured && matchedServer ? {
            actionLabel: 'Configure Auth',
            onAction: () => {
              setNotificationModal({ open: false, title: '', message: '' });
              handleOpenAuthModal(matchedServer);
            }
          } : {})
        });
        fetchServers();
      } else {
        setError('Validation failed');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setError('Error validating server');
    } finally {
      setValidating(null);
    }
  };

  const handleHealthCheck = async (serverId: string) => {
    try {
      setHealthChecking(serverId);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${serverId}/health-check`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setNotificationModal({
          open: true,
          title: t('pages.mCPServers.healthCheckResult'),
          message: t('pages.mCPServers.serverHealthCheckCompleted'),
          details: (
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
              <p><strong>{t('pages.mCPServers.status')}</strong> {data.status}</p>
              <p><strong>{t('pages.mCPServers.responseTime')}</strong> {data.response_time_ms}{t('pages.mCPServers.msUnit')}</p>
            </div>
          )
        });
        fetchServers();
      } else {
        setError('Health check failed');
      }
    } catch (error) {
      console.error('Health check error:', error);
      setError('Error checking server health');
    } finally {
      setHealthChecking(null);
    }
  };

  const handleViewServerDetails = async (server: MCPServer) => {
    try {
      setSelectedServer(server);
      setLoadingTools(true);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${server.server_id}/tools`);
      if (response.ok) {
        const data = await response.json();
        setServerTools(data.tools || []);
      }
    } catch (error) {
      console.error('Error loading tools:', error);
      setServerTools([]);
    } finally {
      setLoadingTools(false);
      setShowServerDetailsModal(true);
    }
  };

  const handleConfigureAuth = async () => {
    if (!selectedServer) return;
    try {
      setError(null);

      if (authFormData.auth_type === 'basic' && (!authFormData.username || !authFormData.password)) {
        setError('Basic auth requires username and password');
        return;
      }

      if ((authFormData.auth_type === 'bearer_token' || authFormData.auth_type === 'api_key') && !authFormData.token) {
        setError(authFormData.auth_type === 'bearer_token' ? 'Bearer token is required' : 'API key is required');
        return;
      }

      if (authFormData.auth_type === 'jwt' && (!authFormData.secret || !authFormData.algorithm)) {
        setError('JWT auth requires secret and algorithm');
        return;
      }

      if (
        authFormData.auth_type === 'oauth2' &&
        (!authFormData.client_id || !authFormData.client_secret || !authFormData.token_url)
      ) {
        setError('OAuth2 requires client ID, client secret, and token URL');
        return;
      }

      let credentials: any = {};
      if (authFormData.auth_type === 'basic') {
        credentials = { username: authFormData.username, password: authFormData.password };
      } else if (authFormData.auth_type === 'bearer_token' || authFormData.auth_type === 'api_key') {
        credentials = { token: authFormData.token };
      } else if (authFormData.auth_type === 'jwt') {
        credentials = { secret: authFormData.secret, algorithm: authFormData.algorithm };
      } else if (authFormData.auth_type === 'oauth2') {
        credentials = {
          client_id: authFormData.client_id,
          client_secret: authFormData.client_secret,
          token_url: authFormData.token_url,
        };
      }
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${selectedServer.server_id}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_type: authFormData.auth_type, credentials }),
      });
      if (response.ok) {
        setNotificationModal({
          open: true,
          title: t('pages.mCPServers.success'),
          message: t('pages.mCPServers.authenticationConfiguredSuccessfully')
        });
        setShowAuthModal(false);
        fetchServers();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to configure auth');
      }
    } catch (error) {
      console.error('Auth config error:', error);
      setError('Error configuring authentication');
    }
  };

  const handleOpenAuthModal = async (server: MCPServer) => {
    setSelectedServer(server);
    setExistingAuthType(null);
    setLoadingAuth(true);
    setShowAuthModal(true);
    try {
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${server.server_id}`);
      if (response.ok) {
        const data = await response.json();
        const fetchedAuthType = data.auth?.auth_type || server.auth_type;
        setExistingAuthType(fetchedAuthType !== 'none' ? fetchedAuthType : null);
        setAuthFormData((prev) => ({
          ...prev,
          auth_type: fetchedAuthType && fetchedAuthType !== 'none' ? fetchedAuthType : prev.auth_type,
        }));
      }
    } catch {
      // fallback to server row value
      const fallback = server.auth_type;
      setExistingAuthType(fallback !== 'none' ? fallback : null);
      setAuthFormData((prev) => ({
        ...prev,
        auth_type: fallback && fallback !== 'none' ? fallback : prev.auth_type,
      }));
    } finally {
      setLoadingAuth(false);
    }
  };

  const handleDeleteServer = async (serverId: string) => {
    try {
      setDeleting(serverId);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/mcp/servers/${serverId}`, { method: 'DELETE' });
      if (response.ok) {
        setShowDeleteConfirm(null);
        fetchServers();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete server');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Error deleting server');
    } finally {
      setDeleting(null);
    }
  };

  const activeCount = servers.filter((s) => s.status === 'active').length;
  const healthyCount = servers.filter((s) => s.health_status === 'healthy').length;
  const pendingCount = servers.filter((s) => s.status === 'pending').length;

  const inputField = (id: string, label: string, value: string, onChange: (v: string) => void, type = 'text', placeholder = '') => (
    <div className="ent-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="ent-input" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="server" size={20} /> {t('pages.mCPServers.mcpServerManagement')}
          </h1>
          <p className="subtitle">{t('pages.mCPServers.discoverRegisterAndManageModelContext')}</p>
          <p className="subtitle">{t('pages.mCPServers.cloudGatewayHint')} <Link to="/mcp-gateway">{t('pages.mCPServers.cloudGatewayLink')}</Link>.</p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <button className="ent-btn primary" onClick={() => setShowDiscoveryModal(true)}>
            <Icon name="search" />
            {t('pages.mCPServers.discover')}
          </button>
          <button
            className="ent-btn"
            onClick={() => {
              setFormData({ name: '', hostname: '', port: 3000, protocol: 'http', auth_type: 'none' });
              setShowAddServerModal(true);
            }}
          >
            <Icon name="plus" />
            {t('pages.mCPServers.addServer')}
          </button>
          <button className="ent-btn" onClick={() => { setIssuedToken(null); setShowBrokerModal(true); }}>
            <Icon name="shield" />
            {t('pages.mCPServers.brokerSensors')}
            {brokerSensors.some((s) => s.connected) && (
              <span className="badge badge-success" style={{ marginLeft: 6 }}>
                {brokerSensors.filter((s) => s.connected).length}
              </span>
            )}
          </button>
          <button className="ent-btn" onClick={fetchServers} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} />
            {t('pages.mCPServers.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" />
          {error}
        </div>
      )}

      {discoveryResults && (
        <div className="notice success">
          <Icon name="checkCircle" />
          {t('pages.mCPServers.discoveryComplete', { discovered: discoveryResults.total_discovered, validated: discoveryResults.total_validated })}
          <button className="ent-btn ghost sm spacer" onClick={() => setDiscoveryResults(null)}>
            {t('pages.mCPServers.dismiss')}
          </button>
        </div>
      )}

      {/* Summary metrics */}
      <div className="metric-row">
        <div className="metric accent-info">
          <span className="label">
            <Icon name="server" /> {t('pages.mCPServers.totalServers')}
          </span>
          <div className="value">{servers.length}</div>
        </div>
        <div className="metric accent-success">
          <span className="label">
            <Icon name="checkBadge" /> {t('pages.mCPServers.active')}
          </span>
          <div className="value">{activeCount}</div>
        </div>
        <div className="metric accent-success">
          <span className="label">
            <Icon name="signal" /> {t('pages.mCPServers.healthy')}
          </span>
          <div className="value">{healthyCount}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="bell" /> {t('pages.mCPServers.pending')}
          </span>
          <div className="value">{pendingCount}</div>
        </div>
      </div>

      {/* Gateway Access URIs */}
      {availableServices.length > 0 && (
        <div className="ent-card" style={{ marginBottom: 18 }}>
          <div className="ent-card-head">
            <h3>
              <Icon name="server" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              {t('pages.mCPServers.gatewayAccessURIs')}
            </h3>
            <span className="muted-text" style={{ fontSize: 12 }}>{t('pages.mCPServers.available', { v0: availableServices.length })}</span>
          </div>
          <div className="ent-card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8 }}>
            {availableServices.map((service) => {
              // Match the available service to a registered server to recover its
              // upstream hostname for the favicon; hardcoded/internal services
              // without a public domain fall back to the generic server icon.
              const matchedServer = servers.find((s) => s.name === service.name);
              const logoHost = matchedServer?.hostname || service.name;
              return (
              <div key={service.name} className="gateway-uri">
                <div className="gateway-uri-head">
                  <span className="gateway-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ServerLogo hostname={logoHost} apiUrl={apiUrl} proxyService={service.name} size={18} />
                    <span>{service.name}</span>
                  </span>
                  <CodeToken muted>{service.type}</CodeToken>
                </div>
                <span className="copy-cell">
                  <code title={service.gateway_uri}>{service.gateway_uri}</code>
                  <CopyButton value={service.gateway_uri} />
                </span>
                {service.status && (
                  <div className="gateway-meta">
                    {service.status}
                    {service.health_status && ` · ${service.health_status}`}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="ent-toolbar">
        <div className="seg">
          {['all', 'pending', 'validated', 'active', 'disabled', 'unreachable'].map((status) => (
            <button key={status} className={statusFilter === status ? 'active' : ''} onClick={() => setStatusFilter(status)}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Servers table */}
      {loading ? (
        <p className="muted-text">{t('pages.mCPServers.loadingServers')}</p>
      ) : servers.length > 0 ? (
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th>{t('pages.mCPServers.name')}</th>
                <th>{t('pages.mCPServers.address')}</th>
                <th style={{ width: 110 }}>{t('pages.mCPServers.status2')}</th>
                <th style={{ width: 120 }}>{t('pages.mCPServers.health2')}</th>
                <th style={{ width: 110 }}>{t('pages.mCPServers.auth2')}</th>
                <th style={{ width: 120 }}>{t('pages.mCPServers.access')}</th>
                <th style={{ width: 110 }}>{t('pages.mCPServers.created')}</th>
                <th style={{ width: 250, textAlign: 'right' }}>{t('pages.mCPServers.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {servers.map((server) => (
                <tr key={server.server_id}>
                  <td style={{ fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ServerLogo hostname={server.hostname} apiUrl={apiUrl} proxyService={server.name} />
                      <span>{server.name}</span>
                    </div>
                  </td>
                  <td className="mono" style={{ color: 'var(--text-2)' }}>
                    {server.hostname}:{server.port}
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[server.status] || 'badge-unknown'}`}>{server.status}</span>
                    {server.status === 'validated' && (
                      // 'validated' proves reachability but routes nothing — agents
                      // only ever see 'active' servers. Say so here rather than
                      // leaving a healthy server silently missing from the agent list.
                      <div
                        style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 3, whiteSpace: 'nowrap' }}
                        title={t('pages.mCPServers.agentsCanOnlyUseActiveServers')}
                      >
                        {t('pages.mCPServers.notYetActive')}
                      </div>
                    )}
                  </td>
                  <td>
                    <StatusPulse state={server.health_status} />
                  </td>
                  <td>
                    <CodeToken muted>{server.auth_type}</CodeToken>
                  </td>
                  <td>
                    {server.access_mode === 'brokered' ? (
                      <span
                        className="badge badge-info"
                        title={
                          brokerSensors.find((s) => s.sensor_id === server.sensor_id)?.name
                            ? `Relayed via ${brokerSensors.find((s) => s.sensor_id === server.sensor_id)!.name}`
                            : t('pages.mCPServers.relayedViaAnOnPremBroker')
                        }
                      >
                        {t('pages.mCPServers.brokered')}
                      </span>
                    ) : (
                      <CodeToken muted>{t('pages.mCPServers.direct')}</CodeToken>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-2)' }}>{parseServerDate(server.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div className="action-group">
                        <button
                          className="icon-btn"
                          aria-label={t('pages.mCPServers.viewDetails')}
                          data-tooltip="View details"
                          onClick={() => handleViewServerDetails(server)}
                        >
                          <Icon name="view" />
                        </button>
                        <button
                          className="icon-btn"
                          aria-label={t('pages.mCPServers.configureAuth2')}
                          data-tooltip="Configure auth"
                          onClick={() => handleOpenAuthModal(server)}
                        >
                          <Icon name="key" />
                        </button>
                        <button
                          className="icon-btn"
                          aria-label={t('pages.mCPServers.accessMode')}
                          data-tooltip="Access mode (direct / brokered)"
                          onClick={() => handleOpenAccessModal(server)}
                        >
                          <Icon name="server" />
                        </button>
                        <button
                          className="icon-btn"
                          aria-label={t('pages.mCPServers.validate')}
                          data-tooltip="Validate"
                          onClick={() => handleValidateServer(server.server_id)}
                          disabled={validating === server.server_id}
                        >
                          <Icon name={validating === server.server_id ? 'refresh' : 'checkCircle'} className={validating === server.server_id ? 'spin' : ''} />
                        </button>
                        <button
                          className={`icon-btn${server.status === 'active' ? '' : ' primary'}`}
                          aria-label={server.status === 'active' ? t('pages.mCPServers.deactivate') : t('pages.mCPServers.activate')}
                          data-tooltip={
                            server.status === 'active'
                              ? 'Deactivate — stops routing agent traffic here'
                              : server.status === 'validated'
                                ? 'Activate — makes this server available to agents'
                                : 'Activate (not validated yet — validate first if you can)'
                          }
                          onClick={() => handleSetActive(server, server.status !== 'active')}
                          disabled={togglingActive === server.server_id}
                        >
                          <Icon
                            name={togglingActive === server.server_id ? 'refresh' : 'power'}
                            className={togglingActive === server.server_id ? 'spin' : ''}
                          />
                        </button>
                        <button
                          className="icon-btn"
                          aria-label={t('pages.mCPServers.runHealthCheck')}
                          data-tooltip="Run health check"
                          onClick={() => handleHealthCheck(server.server_id)}
                          disabled={healthChecking === server.server_id}
                        >
                          <Icon name={healthChecking === server.server_id ? 'refresh' : 'signal'} className={healthChecking === server.server_id ? 'spin' : ''} />
                        </button>
                        <button
                          className="icon-btn danger"
                          aria-label={t('pages.mCPServers.delete')}
                          data-tooltip="Delete"
                          onClick={() => setShowDeleteConfirm(server.server_id)}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ent-empty">
          <span className="icon">
            <Icon name="server" />
          </span>
          <h4>{t('pages.mCPServers.noServersFound')}</h4>
          <p>{t('pages.mCPServers.discoverServersOnYourNetworkOr')}</p>
        </div>
      )}

      {/* Discovery Modal — code editor input */}
      {showDiscoveryModal && (
        <Modal
          title={t('pages.mCPServers.discoverMCPServers')}
          icon="search"
          onClose={() => setShowDiscoveryModal(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowDiscoveryModal(false)} disabled={isDiscovering}>
                {t('pages.mCPServers.cancel')}
              </button>
              <button className="ent-btn primary" onClick={handleDiscoverServers} disabled={isDiscovering}>
                <Icon name="search" className={isDiscovering ? 'spin' : ''} />
                {isDiscovering ? t('pages.mCPServers.discovering') : t('pages.mCPServers.discover')}
              </button>
            </>
          }
        >
          <p className="muted-text" style={{ marginBottom: 14 }}>
            {t('pages.mCPServers.enterOneMCPEndpointPerLine')}
          </p>
          <div className="code-editor">
            <div className="code-editor-bar">
              <span className="dot" style={{ background: 'var(--danger)' }} />
              <span className="dot" style={{ background: 'var(--warning)' }} />
              <span className="dot" style={{ background: 'var(--success)' }} />
              <span style={{ marginLeft: 6 }}>{t('pages.mCPServers.endpointsTxt')}</span>
            </div>
            <textarea
              value={discoveryAddresses}
              onChange={(e) => setDiscoveryAddresses(e.target.value)}
              spellCheck={false}
              placeholder={t('pages.mCPServers.mcp19216811003002')}
            />
          </div>
        </Modal>
      )}

      {/* Access Mode Modal — direct vs. brokered via an on-prem sensor */}
      {accessServer && (
        <Modal
          title={t('pages.mCPServers.accessModeTitle', { name: accessServer.name })}
          icon="server"
          onClose={() => setAccessServer(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setAccessServer(null)}>
                {t('pages.mCPServers.cancel')}
              </button>
              <button
                className="ent-btn primary"
                onClick={handleSaveAccess}
                disabled={savingAccess || (accessMode === 'brokered' && !accessSensorId)}
              >
                {savingAccess ? t('pages.mCPServers.saving') : t('pages.mCPServers.save')}
              </button>
            </>
          }
        >
          <p className="muted-text" style={{ marginTop: 0, fontSize: 13 }}>
            {t('pages.mCPServers.chooseHowAgenti5ReachesThisServer')} <strong>{t('pages.mCPServers.brokered')}</strong> {t('pages.mCPServers.whenTheServerSitsOnPrem')}
          </p>
          <div className="ent-field">
            <label htmlFor="access-mode">{t('pages.mCPServers.accessMode')}</label>
            <Select id="access-mode" value={accessMode} onValueChange={(v) => setAccessMode(v as 'direct' | 'brokered')}>
              <SelectItem value="direct">{t('pages.mCPServers.directAgenti5ConnectsToTheServer')}</SelectItem>
              <SelectItem value="brokered">{t('pages.mCPServers.brokeredRelayedViaAnOnPrem')}</SelectItem>
            </Select>
          </div>
          {accessMode === 'brokered' && (
            <div className="ent-field">
              <label htmlFor="access-sensor">{t('pages.mCPServers.brokerSensor')}</label>
              {brokerSensors.filter((s) => s.status === 'active').length === 0 ? (
                <div className="notice warning" style={{ marginTop: 4 }}>
                  <Icon name="warning" />
                  {t('pages.mCPServers.noBrokerSensorsEnrolledYetRegister')}
                </div>
              ) : (
                <Select id="access-sensor" value={accessSensorId} onValueChange={setAccessSensorId}>
                  {brokerSensors
                    .filter((s) => s.status === 'active')
                    .map((s) => (
                      <SelectItem key={s.sensor_id} value={s.sensor_id}>
                        {s.name} {s.connected ? t('pages.mCPServers.dotConnected') : t('pages.mCPServers.dotOffline')}
                      </SelectItem>
                    ))}
                </Select>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* Broker Sensors Modal — enroll/revoke the on-prem relays */}
      {showBrokerModal && (
        <Modal
          title={t('pages.mCPServers.mcpBrokerSensors')}
          icon="shield"
          onClose={() => setShowBrokerModal(false)}
          footer={
            <button className="ent-btn" onClick={() => setShowBrokerModal(false)}>
              {t('pages.mCPServers.close')}
            </button>
          }
        >
          <p className="muted-text" style={{ marginTop: 0, fontSize: 13 }}>
            {t('pages.mCPServers.aBrokerSensorRunsOnPrem')} <code>AGENTI5_BROKER_TOKEN</code>.
          </p>

          {issuedToken && (
            <div className="notice success" style={{ marginBottom: 12 }}>
              <div>
                <strong>{t('pages.mCPServers.sensorRegistered', { v0: issuedToken.sensor_id })}</strong>
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  {t('pages.mCPServers.copyThisTokenNowItIs')}
                </div>
                <div className="copy-cell" style={{ marginTop: 8 }}>
                  <code>{issuedToken.token}</code>
                  <CopyButton value={issuedToken.token} />
                </div>
              </div>
            </div>
          )}

          <div className="ent-field">
            <label htmlFor="sensor-name">{t('pages.mCPServers.registerANewSensor')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="sensor-name"
                className="ent-input"
                value={newSensorName}
                onChange={(e) => setNewSensorName(e.target.value)}
                placeholder={t('pages.mCPServers.eGDatacenterEast')}
              />
              <button
                className="ent-btn primary"
                onClick={handleRegisterSensor}
                disabled={registeringSensor || !newSensorName.trim()}
              >
                <Icon name="plus" />
                {registeringSensor ? t('pages.mCPServers.registering') : t('pages.mCPServers.register')}
              </button>
            </div>
          </div>

          {brokerSensors.length > 0 && (
            <div className="ent-table-wrap" style={{ marginTop: 12 }}>
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{t('pages.mCPServers.name')}</th>
                    <th style={{ width: 110 }}>{t('pages.mCPServers.state')}</th>
                    <th style={{ width: 150 }}>{t('pages.mCPServers.lastPoll')}</th>
                    <th style={{ width: 90, textAlign: 'right' }}>{t('pages.mCPServers.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {brokerSensors.map((s) => (
                    <tr key={s.sensor_id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.name}</div>
                        <CodeToken muted>{s.sensor_id}</CodeToken>
                      </td>
                      <td>
                        {s.status === 'revoked' ? (
                          <span className="badge badge-error">{t('pages.mCPServers.revoked')}</span>
                        ) : s.connected ? (
                          <span className="badge badge-success">{t('pages.mCPServers.connected')}</span>
                        ) : (
                          <span className="badge badge-unknown">{t('pages.mCPServers.offline')}</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        {s.last_poll_at ? parseServerDate(s.last_poll_at).toLocaleString() : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {s.status === 'active' && (
                            <button
                              className="icon-btn danger"
                              aria-label={t('pages.mCPServers.revoke')}
                              data-tooltip="Revoke this sensor's token"
                              onClick={() => handleRevokeSensor(s.sensor_id)}
                            >
                              <Icon name="trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {/* Add Server Modal */}
      {showAddServerModal && (
        <Modal
          title={t('pages.mCPServers.addMCPServer')}
          icon="plus"
          onClose={() => setShowAddServerModal(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowAddServerModal(false)}>
                {t('pages.mCPServers.cancel')}
              </button>
              <button className="ent-btn primary" onClick={handleAddServer}>
                <Icon name="plus" />
                {t('pages.mCPServers.addServer')}
              </button>
            </>
          }
        >
          {inputField('server-name', 'Server Name *', formData.name, (v) => setFormData({ ...formData, name: v }), 'text', 'e.g., my-mcp-server')}
          {inputField('server-hostname', 'Hostname *', formData.hostname, (v) => setFormData({ ...formData, hostname: v }), 'text', 'e.g., localhost or 192.168.1.1')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="ent-field">
              <label htmlFor="server-port">{t('pages.mCPServers.port')}</label>
              <input
                id="server-port"
                className="ent-input"
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                placeholder="3000"
              />
            </div>
            <div className="ent-field">
              <label htmlFor="server-protocol">{t('pages.mCPServers.protocol')}</label>
              <Select
                id="server-protocol"
                value={formData.protocol}
                onValueChange={(v) => setFormData({ ...formData, protocol: v as 'http' | 'https' })}
              >
                <SelectItem value="http">{t('pages.mCPServers.http')}</SelectItem>
                <SelectItem value="https">{t('pages.mCPServers.https')}</SelectItem>
              </Select>
            </div>
          </div>
        </Modal>
      )}

      {/* Server Details Modal */}
      {showServerDetailsModal && selectedServer && (
        <Modal
          title={t('pages.mCPServers.serverDetailsTitle', { name: selectedServer.name })}
          icon="view"
          onClose={() => setShowServerDetailsModal(false)}
          footer={
            <button className="ent-btn" onClick={() => setShowServerDetailsModal(false)}>
              {t('pages.mCPServers.close')}
            </button>
          }
        >
          <div className="detail-grid">
            <div>
              <span className="detail-label">{t('pages.mCPServers.serverID')}</span>
              <span className="copy-cell">
                <CodeToken>{selectedServer.server_id.substring(0, 18)}…</CodeToken>
                <CopyButton value={selectedServer.server_id} />
              </span>
            </div>
            <div>
              <span className="detail-label">{t('pages.mCPServers.status2')}</span>
              <span className={`badge ${STATUS_BADGE[selectedServer.status] || 'badge-unknown'}`}>{selectedServer.status}</span>
            </div>
            <div>
              <span className="detail-label">{t('pages.mCPServers.address')}</span>
              <CodeToken muted>{selectedServer.hostname}:{selectedServer.port}</CodeToken>
            </div>
            <div>
              <span className="detail-label">{t('pages.mCPServers.health2')}</span>
              <StatusPulse state={selectedServer.health_status} />
            </div>
            <div>
              <span className="detail-label">{t('pages.mCPServers.protocol')}</span>
              <CodeToken muted>{selectedServer.protocol.toUpperCase()}</CodeToken>
            </div>
            <div>
              <span className="detail-label">{t('pages.mCPServers.authType')}</span>
              <CodeToken muted>{selectedServer.auth_type}</CodeToken>
            </div>
          </div>

          <h3 style={{ fontSize: 13, fontWeight: 700, margin: '20px 0 10px' }}>{t('pages.mCPServers.availableTools')}</h3>
          {loadingTools ? (
            <p className="muted-text">{t('pages.mCPServers.loadingTools')}</p>
          ) : serverTools.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {serverTools.map((tool) => (
                <div key={tool.tool_id} className="tool-detail">
                  <div className="tool-detail-name">{tool.name}</div>
                  <div className="tool-detail-desc">{tool.description || t('pages.mCPServers.noDescription')}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted-text">{t('pages.mCPServers.noToolsDiscoveredYet')}</p>
          )}
        </Modal>
      )}

      {/* Auth Modal */}
      {showAuthModal && selectedServer && (
        <Modal
          title={t('pages.mCPServers.configureAuthTitle', { name: selectedServer.name })}
          icon="key"
          onClose={() => { setShowAuthModal(false); setExistingAuthType(null); }}
          footer={
            <>
              <button className="ent-btn" onClick={() => { setShowAuthModal(false); setExistingAuthType(null); }}>
                {t('pages.mCPServers.cancel')}
              </button>
              <button className="ent-btn primary" onClick={handleConfigureAuth} disabled={loadingAuth}>
                <Icon name="lock" />
                {t('pages.mCPServers.saveAuthentication')}
              </button>
            </>
          }
        >
          {loadingAuth ? (
            <p className="muted-text" style={{ marginBottom: 14 }}>{t('pages.mCPServers.loadingCurrentAuthConfiguration')}</p>
          ) : existingAuthType ? (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--info-subtle, rgba(59,130,246,0.08))', borderRadius: 6, border: '1px solid var(--info, #3b82f6)', fontSize: 13 }}>
              <strong>{t('pages.mCPServers.currentlyConfigured')}</strong> <code>{existingAuthType}</code>
              <span style={{ color: 'var(--text-2)', marginLeft: 8 }}>{t('pages.mCPServers.credentialsAreStoredSecurelyAndCannot')}</span>
            </div>
          ) : null}
          <div className="ent-field">
            <label htmlFor="auth-type">{t('pages.mCPServers.authenticationType')}</label>
            <Select
              id="auth-type"
              value={authFormData.auth_type}
              onValueChange={(v) => setAuthFormData({ ...authFormData, auth_type: v })}
            >
              <SelectItem value="none">{t('pages.mCPServers.none')}</SelectItem>
              <SelectItem value="basic">{t('pages.mCPServers.basicAuth')}</SelectItem>
              <SelectItem value="bearer_token">{t('pages.mCPServers.bearerToken')}</SelectItem>
              <SelectItem value="api_key">{t('pages.mCPServers.apiKey')}</SelectItem>
              <SelectItem value="jwt">{t('pages.mCPServers.jwt')}</SelectItem>
              <SelectItem value="oauth2">{t('pages.mCPServers.oauth2')}</SelectItem>
            </Select>
          </div>

          {authFormData.auth_type === 'basic' && (
            <>
              {inputField('auth-username', 'Username', authFormData.username, (v) => setAuthFormData({ ...authFormData, username: v }), 'text', 'admin')}
              {inputField('auth-password', 'Password', authFormData.password, (v) => setAuthFormData({ ...authFormData, password: v }), 'password', '••••••••')}
            </>
          )}

          {(authFormData.auth_type === 'bearer_token' || authFormData.auth_type === 'api_key') &&
            inputField(
              'auth-token',
              authFormData.auth_type === 'bearer_token' ? 'Bearer Token' : 'API Key',
              authFormData.token,
              (v) => setAuthFormData({ ...authFormData, token: v }),
              'password',
              'sk-1234567890abcdef'
            )}

          {authFormData.auth_type === 'jwt' && (
            <>
              {inputField('auth-secret', 'Secret Key', authFormData.secret, (v) => setAuthFormData({ ...authFormData, secret: v }), 'password', 'your-secret-key')}
              <div className="ent-field">
                <label htmlFor="auth-algorithm">{t('pages.mCPServers.algorithm')}</label>
                <Select
                  id="auth-algorithm"
                  value={authFormData.algorithm}
                  onValueChange={(v) => setAuthFormData({ ...authFormData, algorithm: v })}
                >
                  <SelectItem value="HS256">{t('pages.mCPServers.hs256')}</SelectItem>
                  <SelectItem value="RS256">{t('pages.mCPServers.rs256')}</SelectItem>
                </Select>
              </div>
            </>
          )}

          {authFormData.auth_type === 'oauth2' && (
            <>
              {inputField('oauth-client-id', 'Client ID', authFormData.client_id, (v) => setAuthFormData({ ...authFormData, client_id: v }), 'text', 'client_id')}
              {inputField('oauth-client-secret', 'Client Secret', authFormData.client_secret, (v) => setAuthFormData({ ...authFormData, client_secret: v }), 'password', 'client_secret')}
              {inputField('oauth-token-url', 'Token URL', authFormData.token_url, (v) => setAuthFormData({ ...authFormData, token_url: v }), 'text', 'https://auth.example.com/oauth/token')}
            </>
          )}
        </Modal>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Modal
          title={t('pages.mCPServers.deleteServer')}
          icon="warning"
          onClose={() => setShowDeleteConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowDeleteConfirm(null)} disabled={deleting === showDeleteConfirm}>
                {t('pages.mCPServers.cancel')}
              </button>
              <button className="ent-btn danger" onClick={() => handleDeleteServer(showDeleteConfirm)} disabled={deleting === showDeleteConfirm}>
                <Icon name="trash" />
                {deleting === showDeleteConfirm ? t('pages.mCPServers.deleting') : t('pages.mCPServers.delete')}
              </button>
            </>
          }
        >
          <p className="muted-text">{t('pages.mCPServers.areYouSureYouWantTo')}</p>
        </Modal>
      )}

      {/* Notification Modal */}
      {notificationModal.open && (
        <Modal
          title={notificationModal.title}
          icon="checkCircle"
          onClose={() => setNotificationModal({ open: false, title: '', message: '' })}
          footer={
            <>
              {notificationModal.onAction && (
                <button className="ent-btn" onClick={notificationModal.onAction}>
                  <Icon name="key" /> {notificationModal.actionLabel || t('pages.mCPServers.action')}
                </button>
              )}
              <button
                className="ent-btn primary"
                onClick={() => setNotificationModal({ open: false, title: '', message: '' })}
              >
                <Icon name="check" /> {t('pages.mCPServers.ok')}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 12 }}>{notificationModal.message}</p>
          {notificationModal.details && notificationModal.details}
        </Modal>
      )}
    </div>
  );
};

export default MCPServers;
